import React, { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, AgGridReact, Flex, Grid } from "@jsq/ds/core";
import { toGlobalId } from "#utils/graphql_utils";
import { DistributionSlideout } from "../DistributionSlideout";
import { getColumnDefinitions, SupportedColumns } from "./columnDefinitions";
import { DistributionBatchesGraphqlDataSource } from "./distributionBatchesGQLDataSource";
import { tableContainer } from "./styles_vanilla.css";
import { DistributionBatchesTableProps, DistributionBatchRowItem } from "./types";

const rowCountPerPage = 20;

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true, // Allows text to be copied from cells
  pagination: true,
  paginationPageSize: rowCountPerPage, // Sets the number of rows per page
  paginationPageSizeSelector: false, // Disallows user from changing the page size
  rowHeight: 50, // Fixed row height for consistent button alignment
  rowSelection: "single", // Only allows one row to be selected at a time
  suppressCellFocus: true,
  suppressContextMenu: true,
};

const defaultColumnOptions = {
  editable: false,
  flex: 1, // enables the columns to use the whole table width
  minWidth: 100,
  sortable: false,
};

const getRowId = (params: AgGrid.GetRowIdParams) => {
  return toGlobalId(
    "DistributionBatchObjectType",
    (params.data as DistributionBatchRowItem).id,
  );
};

const sideBarDef: AgGrid.SideBarDef = {
  toolPanels: [
    {
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
      minWidth: 280,
      width: 280,
    },
  ],
  position: "right",
};

export const DistributionBatchesTable: React.FC<DistributionBatchesTableProps> = ({
  paymentsContext,
  selectedDistributionBatchId,
  onSelectDistributionBatch,
  onCloseSlideout,
  onPayClick,
  onReviewClick,
  onPaymentBatchReviewClick,
  selectedPaymentBatchId,
  onReviewSlideoutClose,
  showActionColumn = false,
}: DistributionBatchesTableProps) => {
  const client = useApolloClient();

  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new DistributionBatchesGraphqlDataSource(client, rowCountPerPage);
  }, [client]);

  const columns = useMemo(() => {
    const allColumns = getColumnDefinitions({ onPayClick, onReviewClick });
    if (showActionColumn) {
      return allColumns;
    }
    return allColumns.filter((col) => col.field !== SupportedColumns.Action);
  }, [showActionColumn, onPayClick, onReviewClick]);

  const onRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<DistributionBatchRowItem>) => {
      // Ignore clicks on the action button
      const clickedElement = event.event?.target as HTMLElement;
      if (clickedElement?.closest('[data-testid^="action-button"]')) {
        return;
      }
      const distributionRowDetails = event.data;
      if (distributionRowDetails) {
        onSelectDistributionBatch?.(distributionRowDetails);
      }
    },
    [onSelectDistributionBatch],
  );

  const handleCreatePayment = useCallback(
    (distribution: DistributionBatchRowItem) => {
      // Close the slideout first, then trigger the pay workflow
      onCloseSlideout?.();
      onPayClick?.(distribution);
    },
    [onCloseSlideout, onPayClick],
  );

  return (
    <>
      <Flex className={tableContainer}>
        <Grid<DistributionBatchRowItem>
          key="distribution_batches_grid"
          cacheBlockSize={rowCountPerPage}
          columnDefs={columns}
          defaultColDef={defaultColumnOptions}
          getRowId={getRowId}
          gridId="distribution_batches"
          gridOptions={gridOptions}
          onRowClicked={onRowClicked}
          /*
           * @link https://www.ag-grid.com/react-data-grid/upgrading-to-ag-grid-31-1/
           * reactiveCustomComponents will be on by default for v32 or later.
           * This prop is needed in order for the custom filters to recevie the newer callbacks
           * e.g. onModelChange, etc
           */
          reactiveCustomComponents
          rowModelType="serverSide"
          serverSideDatasource={gridDataSource}
          sideBar={sideBarDef}
        />
      </Flex>
      {selectedDistributionBatchId && (
        <DistributionSlideout
          isOpen={true}
          onClose={onCloseSlideout ?? (() => {})}
          distributionBatchId={selectedDistributionBatchId}
          onCreatePayment={onPayClick ? handleCreatePayment : undefined}
          onReviewClick={onPaymentBatchReviewClick}
          selectedPaymentBatchId={selectedPaymentBatchId}
          onReviewSlideoutClose={onReviewSlideoutClose}
          paymentsContext={paymentsContext}
        />
      )}
    </>
  );
};

export default DistributionBatchesTable;
